import { events } from "@constants/tracking";

import { formatTimeString } from "@helpers/date";
import { isOlxExamWithinDateRange } from "@helpers/exams";
import { useAppStore } from "@hooks/useAppStore";
import { getPlatform } from "@memorang/helpers/src/common";
import type { ExamDetails } from "@memorang/types/src/exams";
import { trackCustomEvent } from "analytics";
import { fetchExamDetails, fetchExamsList } from "api/commerce/exam-service";
import type { ApplyAccessCodeResponse } from "api/commerce/types";
import type { AccessCodeData } from "api/commerce/types";
import { DateTime } from "luxon";
import type { Dispatch } from "react";
import type { SetStateAction } from "react";
export const getRedeemCodeSuccessText = (accessCodeResult: AccessCodeData) => {
	const { bundleName, expiryDate, planLength } = accessCodeResult;
	const expiryText = DateTime.fromISO(expiryDate).toFormat("MMMM dd, yyyy");
	return `You now have **${planLength} access** to the **${bundleName}**! This will expire on **${expiryText}**`;
};

export const getOlxPostRedeemDialogConfig = (
	beginStartDate: string,
	endStartDate: string,
	examName: string,
	bundleName: string,
) => {
	const now = Date.now();
	const beginTime = new Date(beginStartDate).getTime();
	const endTime = new Date(endStartDate).getTime();
	let title = "Access Unlocked";
	let body = "";
	let confirmCta: string | undefined;
	let cancelCta = "OK";

	const testInfo = `**Exam:** ${examName} | **Test:** ${bundleName}

---
`;

	if (now < beginTime) {
		const daysUntilStart = Math.ceil((beginTime - now) / (1000 * 60 * 60 * 24));
		body = `${testInfo}
Your access to the OLX test has been unlocked!

You can start your session in **${daysUntilStart} day(s)**. Please check back later to begin your session from the Practice Tests tab.`;
	} else if (isOlxExamWithinDateRange(beginStartDate, endStartDate)) {
		body = `${testInfo}
Your access to the OLX test is now active!

You can start your session anytime before **${formatTimeString(endStartDate)}**.

Would you like to begin your session now or start it later?`;
		confirmCta = "Start Now";
		cancelCta = "Start Later";
	} else if (now > endTime) {
		title = "Access Expired";
		body = `${testInfo}
Your access to the OLX test has expired on **${formatTimeString(endStartDate)}**.

Please contact your professor or school administrator for further assistance.`;
	}

	return {
		title,
		body,
		confirmCta,
		cancelCta,
	};
};

export const getSupportDetails = (
	appName: string,
	username: string,
	email: string,
) => {
	const version = useAppStore.getState().app.version;
	const platform = getPlatform();
	const subject = `[${appName}] Access Code Issue`;
	const body = `Hey ${appName} support, my username is ${username} and email is ${email}. I'm using version ${version} of the ${appName} ${platform} app. I'm having some trouble with my access code. I've included it here: [copy paste your access code here]
  
Thank you!`;

	return { subject, body, version, platform };
};

export const switchExamContext = async (
	examId: string,
	handleSwitchExamContext: (examDetails: ExamDetails) => Promise<void>,
) => {
	try {
		const examDetailsLatest = await fetchExamDetails({ examId });

		if (examDetailsLatest) {
			// console.info("examDetailsLatest", examDetailsLatest);
			await handleSwitchExamContext(examDetailsLatest);
			return;
		}
		trackCustomEvent({
			eventName: events.settingExamContextFromExamDetailsFailed,
			examId,
			examDetailsResult: JSON.stringify(examDetailsLatest),
		});

		const exams = await fetchExamsList();
		const examDetails = exams.find(
			(examDetails) => examDetails.exam.id === examId,
		);
		if (examDetails) {
			await handleSwitchExamContext(examDetails);
		}
	} catch (error) {
		trackCustomEvent({
			eventName: events.postRedeemExamContextSwitchFailed,
			examId,
			error: JSON.stringify(error),
		});
	}
};

export const handleAccessCodeResult = async (
	result: ApplyAccessCodeResponse,
	handleSwitchExamContext: (examDetails: ExamDetails) => Promise<void>,
	setOlxDates: Dispatch<
		SetStateAction<{
			beginStartDate: string;
			endStartDate: string;
			tempAccessCodeResponse?: ApplyAccessCodeResponse;
		} | null>
	>,
) => {
	trackCustomEvent({
		eventName: events.promoCodeRedeemed,
		examId: result.data.examId,
		examName: result.data.examName,
	});
	await switchExamContext(result.data.examId, handleSwitchExamContext);
	if (
		result.data.beginStartDate &&
		result.data.endStartDate &&
		!result.isSameEmail
	) {
		setOlxDates({
			beginStartDate: result.data.beginStartDate,
			endStartDate: result.data.endStartDate,
			tempAccessCodeResponse: result,
		});
		return { shouldHandleNext: false };
	}

	return { shouldHandleNext: true };
};
